/** Bounded arithmetic parser. Never evaluates JavaScript or arbitrary identifiers. */
export function calculateExpression(expression: string): number {
  const input = expression.replace(/×/g, '*').replace(/[÷]/g, '/').replace(/−/g, '-').replace(/\s/g, '');
  if (!input || input.length > 300) throw Error('Enter an expression of up to 300 characters.');
  const tokens = input.match(/(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|[()+\-*/^%]/g);
  if (!tokens || tokens.join('') !== input) throw Error('Use numbers, parentheses and + − × ÷ ^ %.');
  let index = 0;
  function primary(): number {
    const token = tokens![index++];
    let value: number;
    if (token === '(') {
      value = sum();
      if (tokens![index++] !== ')') throw Error('Close every parenthesis.');
    } else {
      if (!token || !/^(?:\d|\.)/.test(token)) throw Error('Enter a number after each operator.');
      value = Number(token);
    }
    while (tokens![index] === '%') { index++; value /= 100; }
    return value;
  }
  function power(): number {
    const base = primary();
    if (tokens![index] === '^') { index++; return base ** unary(); }
    return base;
  }
  function unary(): number {
    if (tokens![index] === '+') { index++; return unary(); }
    if (tokens![index] === '-') { index++; return -unary(); }
    return power();
  }
  function product(): number {
    let value = unary();
    while (tokens![index] === '*' || tokens![index] === '/') {
      const operation = tokens![index++];
      const right = unary();
      if (operation === '/' && right === 0) throw Error('Division by zero is undefined.');
      value = operation === '*' ? value * right : value / right;
    }
    return value;
  }
  function sum(): number {
    let value = product();
    while (tokens![index] === '+' || tokens![index] === '-') {
      const operation = tokens![index++];
      const right = product();
      value = operation === '+' ? value + right : value - right;
    }
    return value;
  }
  const result = sum();
  if (index !== tokens.length) throw Error('Check the operators and parentheses.');
  if (!Number.isFinite(result)) throw Error('This expression has no finite real result.');
  return Number(result.toPrecision(14));
}
